// API wrapper for the Email Intel drafts feature (backend: a new email_intel
// module, proposed to be mounted at /email-intel on the common backend -
// see the message sent to the backend sessions for the exact contract this
// was built against). Reuses the shared `API` axios instance from
// src/lib/api.js (JWT-injected via AuthContext) since drafts are a
// logged-in, per-team feature just like lead-engine's runs/companies.
import API from "@/lib/api";

// Turns a Lead Engine company's personalized email (company.ai_proposal)
// into a draft row here. Backend looks the company up by id and copies its
// proposal's subject/body into a new draft - the frontend doesn't send the
// email content itself, just the pointer, so the draft always starts as an
// exact copy of what Lead Engine generated.
// `empty` (optional, default false): "Send another mail" on an already-sent
// draft (EmailIntel.jsx) uses this to get a second, blank-subject/body draft
// for the same company instead of re-copying the (already-used) proposal.
export const createDraftFromCompany = (companyId, { empty = false } = {}) =>
  API.post("/email-intel/drafts", { company_id: companyId, empty }).then((res) => res.data);

// "+ Custom email" (EmailIntel.jsx page header) - a Gmail-compose-like blank
// draft with no Lead Engine company behind it at all. From/To/Subject/Body
// all go through the exact same editor + save/send endpoints every other
// draft uses; only its "To" address is user-typed rather than pre-filled
// from a lead's contact info (see DraftDetailPanel's editable `to` field).
//
// `prefill` (optional): lead-finder's "Send to Draft" (LeadsList.jsx) uses
// this same endpoint/shape - its leads have no Lead Engine company_id to
// link back to, so it goes through here instead of createDraftFromCompany,
// passing along whatever it already generated (email/subject/body/name)
// instead of leaving the draft blank. Omitted entirely, this call is
// unchanged from the plain "+ Custom email" button.
export const createCustomDraft = (prefill) =>
  API.post("/email-intel/drafts/custom", prefill || undefined).then((res) => res.data);

export const listDrafts = ({ limit = 20, offset = 0, status } = {}) =>
  API.get("/email-intel/drafts", { params: { limit, offset, ...(status ? { status } : {}) } }).then((res) => res.data);

export const getDraft = (draftId) =>
  API.get(`/email-intel/drafts/${draftId}`).then((res) => res.data);

// Saves a customized subject/body - overwrites the draft in place (per the
// user's ask: "customized email ko save bhi kar sakta h jo database m pehle
// vali mail ko update karde gi"). `email` (optional) is only meaningful for a
// "+ Custom email" draft's editable "To" field - omitted for every other
// draft, whose recipient stays the lead's own contact email.
export const updateDraft = (draftId, { subject, body, email }) =>
  API.patch(`/email-intel/drafts/${draftId}`, { subject, body, email }).then((res) => res.data);

// Sends the draft to the lead's contact email. Takes subject/body directly
// rather than relying on a prior PATCH having landed first - two sequential
// HTTP calls aren't atomic (a dropped connection between them, or another
// tab's edit landing in between, could otherwise send stale content with no
// way to detect it). Pass whatever's currently in the form, edited or not -
// backend both sends it and persists it as the draft's new saved state.
// includeMeetingLink (optional, defaults false): when true, the backend
// appends a "Schedule Meeting" button (Google Calendar Appointment Schedule
// link) to the outgoing email only - it's never saved onto the draft's own
// subject/body, so toggling this off next time leaves nothing behind.
// `email` (optional) - only a "+ Custom email" draft's editable "To" field
// actually sends this; every other draft's caller omits it and the backend
// falls back to the contact email already saved on the draft (unchanged
// behavior - see routes/drafts.py's send_draft docstring).
export const sendDraft = (draftId, { subject, body, includeMeetingLink = false, email }) =>
  API.post(`/email-intel/drafts/${draftId}/send`, {
    subject,
    body,
    include_meeting_link: includeMeetingLink,
    email,
  }).then((res) => res.data);

// Inbox list's per-row delete button (EmailIntel.jsx) - removes the draft
// outright, sent or not (doesn't unsend anything already sent). If it was
// the only draft for its Lead Engine company, the backend also clears that
// company's send_to_draft flag so "Send to Draft" works again there.
export const deleteDraft = (draftId) =>
  API.delete(`/email-intel/drafts/${draftId}`).then((res) => res.data);
